/**
 * Phase 4B — preview scheduler (independent of API generation queue).
 * Concurrency defaults to 1: at most one Worker/iframe preview at a time.
 * API generation must not await this scheduler.
 */

export const PREVIEW_CONCURRENCY = 1;

export type ArtworkPreviewJob = {
  artId: string;
  html: string;
  config: any;
  previewKey: string;
};

export type PreviewSchedulerHandlers = {
  /** True if artwork still exists in application state. */
  isArtPresent: (artId: string) => boolean;
  /** In-memory preview cache lookup (sync, session-only). */
  getCachedPreviewUrl: (previewKey: string) => string | undefined;
  /** Optional async durable lookup — unused after Phase 4E (legacy IDB stored dead blob: URLs). */
  loadCachedPreviewUrl?: (previewKey: string) => Promise<string | null>;
  /** Session-only cache write (must not persist blob: URLs to IndexedDB/localStorage). */
  saveCachedPreviewUrl?: (previewKey: string, url: string) => void;
  /**
   * Run Worker/iframe preview for one artwork.
   * Must use Phase 4A createPreviewBlobUrl ownership for blob: URLs.
   */
  renderPreview: (
    artId: string,
    script: string,
    width: number,
    height: number
  ) => Promise<string | null>;
  /** Apply successful preview (by stable art id). */
  onPreviewReady: (artId: string, url: string, previewKey: string) => void;
  /** Apply failure / allow iframe fallback (by stable art id). */
  onPreviewFailed: (artId: string) => void;
  /** Retain + apply a cache hit URL. */
  onPreviewCacheHit: (artId: string, url: string) => void;
  /** Abandon a blob if art disappeared after Worker returned. */
  abandonPreviewUrl: (url: string, artId: string) => void;
};

export type PreviewSchedulerDiagnostics = {
  pendingCount: number;
  activeCount: number;
  concurrency: number;
  cancelledCount: number;
  completedCount: number;
  failedCount: number;
};

export type PreviewScheduler = {
  enqueue: (job: ArtworkPreviewJob) => void;
  /** Drop queued jobs for artId; in-flight results must still be abandoned by handlers. */
  cancelArt: (artId: string) => void;
  cancelArts: (artIds: string[]) => void;
  getDiagnostics: () => PreviewSchedulerDiagnostics;
  /** Test helper: wait until idle (no pending, no active). */
  whenIdle: () => Promise<void>;
};

function extractScriptAndCanvasSize(html: string): {
  script: string;
  width: number;
  height: number;
} | null {
  const scriptMatch = html.match(/<script>([\s\S]*?)<\/script>/);
  const canvasMatch = html.match(
    /<canvas[^>]*width=["']?(\d+)["']?[^>]*height=["']?(\d+)["']?[^>]*>/i
  );
  if (!scriptMatch || !canvasMatch) return null;
  return {
    script: scriptMatch[1],
    width: parseInt(canvasMatch[1], 10) || 1024,
    height: parseInt(canvasMatch[2], 10) || 1024,
  };
}

export function createPreviewScheduler(
  getHandlers: () => PreviewSchedulerHandlers,
  concurrency: number = PREVIEW_CONCURRENCY
): PreviewScheduler {
  const queue: ArtworkPreviewJob[] = [];
  const cancelled = new Set<string>();
  let activeCount = 0;
  let completedCount = 0;
  let failedCount = 0;
  const idleWaiters: Array<() => void> = [];

  function notifyIdle(): void {
    if (activeCount === 0 && queue.length === 0) {
      while (idleWaiters.length) {
        const w = idleWaiters.shift();
        if (w) w();
      }
    }
  }

  function getDiagnostics(): PreviewSchedulerDiagnostics {
    return {
      pendingCount: queue.length,
      activeCount,
      concurrency,
      cancelledCount: cancelled.size,
      completedCount,
      failedCount,
    };
  }

  async function runJob(job: ArtworkPreviewJob): Promise<void> {
    const handlers = getHandlers();
    if (cancelled.has(job.artId)) {
      return;
    }
    if (!handlers.isArtPresent(job.artId)) {
      // Dropped: art not visible to isArtPresent yet (or already deleted).
      // Callers must sync presence before enqueue (see App processQueue artQueueRef).
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('PREVIEW SKIP (art not present)', job.artId);
      }
      return;
    }

    if (typeof console !== 'undefined' && console.log) {
      console.log('PREVIEW START', job.artId);
    }

    try {
      let cached = handlers.getCachedPreviewUrl(job.previewKey);
      if (!cached && handlers.loadCachedPreviewUrl) {
        cached = (await handlers.loadCachedPreviewUrl(job.previewKey)) || undefined;
      }

      if (cancelled.has(job.artId) || !handlers.isArtPresent(job.artId)) {
        return;
      }

      if (cached) {
        if (handlers.saveCachedPreviewUrl) {
          handlers.saveCachedPreviewUrl(job.previewKey, cached);
        }
        handlers.onPreviewCacheHit(job.artId, cached);
        completedCount += 1;
        if (typeof console !== 'undefined' && console.log) {
          console.log('PREVIEW COMPLETE (cache)', job.artId);
        }
        return;
      }

      const extracted = extractScriptAndCanvasSize(job.html);
      if (!extracted) {
        handlers.onPreviewFailed(job.artId);
        failedCount += 1;
        return;
      }

      const url = await handlers.renderPreview(
        job.artId,
        extracted.script,
        extracted.width,
        extracted.height
      );

      if (cancelled.has(job.artId) || !handlers.isArtPresent(job.artId)) {
        if (url) handlers.abandonPreviewUrl(url, job.artId);
        return;
      }

      if (url != null) {
        if (handlers.saveCachedPreviewUrl) {
          handlers.saveCachedPreviewUrl(job.previewKey, url);
        }
        handlers.onPreviewReady(job.artId, url, job.previewKey);
        completedCount += 1;
        if (typeof console !== 'undefined' && console.log) {
          console.log('PREVIEW COMPLETE', job.artId);
        }
      } else {
        handlers.onPreviewFailed(job.artId);
        failedCount += 1;
      }
    } catch (err) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('PREVIEW JOB ERROR', job.artId, err);
      }
      if (handlers.isArtPresent(job.artId) && !cancelled.has(job.artId)) {
        handlers.onPreviewFailed(job.artId);
      }
      failedCount += 1;
    }
  }

  function pump(): void {
    const startNext = () => {
      if (activeCount >= concurrency || queue.length === 0) {
        notifyIdle();
        return;
      }
      const job = queue.shift()!;
      if (cancelled.has(job.artId)) {
        startNext();
        return;
      }
      activeCount += 1;
      Promise.resolve()
        .then(() => runJob(job))
        .finally(() => {
          activeCount -= 1;
          startNext();
          notifyIdle();
        });
      // Fill remaining concurrency slots (usually 1).
      startNext();
    };
    startNext();
  }

  return {
    enqueue(job: ArtworkPreviewJob) {
      cancelled.delete(job.artId);
      // Replace any older queued job for the same art id (should be rare).
      for (let i = queue.length - 1; i >= 0; i--) {
        if (queue[i].artId === job.artId) queue.splice(i, 1);
      }
      queue.push(job);
      if (typeof console !== 'undefined' && console.log) {
        console.log('PREVIEW ENQUEUE', job.artId, 'queueLength=', queue.length, 'active=', activeCount);
      }
      pump();
    },
    cancelArt(artId: string) {
      cancelled.add(artId);
      for (let i = queue.length - 1; i >= 0; i--) {
        if (queue[i].artId === artId) queue.splice(i, 1);
      }
      notifyIdle();
    },
    cancelArts(artIds: string[]) {
      for (const id of artIds) {
        cancelled.add(id);
      }
      for (let i = queue.length - 1; i >= 0; i--) {
        if (artIds.includes(queue[i].artId)) queue.splice(i, 1);
      }
      notifyIdle();
    },
    getDiagnostics,
    whenIdle() {
      if (activeCount === 0 && queue.length === 0) return Promise.resolve();
      return new Promise((resolve) => idleWaiters.push(resolve));
    },
  };
}

/** Pure helper exported for tests. */
export function __extractScriptAndCanvasSizeForTests(html: string) {
  return extractScriptAndCanvasSize(html);
}
